import React, { useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";

/**
 * Public Data Subject Rights request form (UK GDPR Articles 15, 16, 17, 18, 20).
 * Anyone can lodge — backend assigns a 30-day SLA and pushes into the admin
 * DSAR queue at /admin/compliance-center.
 */
const TYPES = [
  ["access",       "Access — see what data we hold"],
  ["erasure",      "Erasure — delete my data"],
  ["rectification","Rectification — fix incorrect data"],
  ["restrict",     "Restriction — pause processing"],
  ["portability",  "Portability — export my data"],
];

export default function DsarRequest() {
  const [form, setForm] = useState({ type: "access", full_name: "", email: "", phone: "", statement: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/public/dsar`, form);
      setDone(r.data);
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    } catch (err) {
      const d = err?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Submission failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-14 px-5" data-testid="public-dsar">
      <div className="mx-auto max-w-2xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] bg-[#0055FF]/10 text-[#0055FF]">
            <ShieldCheck className="size-3.5" /> Data subject request
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-semibold leading-tight">Your data, your rights.</h1>
          <p className="mt-3 text-slate-600 text-[15px] leading-relaxed max-w-xl mx-auto">
            Under the UK GDPR, you have the right to access, correct, port, restrict or erase the personal data
            we hold on you. Submit a request below — we'll acknowledge within 5 working days and complete
            within 30 days (Article 12(3)).
          </p>
        </div>

        {done ? (
          <Card className="rounded-2xl border-slate-200 shadow-xl shadow-slate-900/[0.04]" data-testid="dsar-success">
            <CardContent className="p-8 text-center space-y-3">
              <div className="size-12 rounded-full mx-auto bg-[#0055FF]/10 text-[#0055FF] grid place-items-center">
                <CheckCircle2 className="size-6" />
              </div>
              <h2 className="font-semibold text-slate-900 text-lg">Request received</h2>
              <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                Your reference is <code className="font-mono text-[12px] bg-slate-100 px-2 py-0.5 rounded">{done.id.slice(0, 8)}</code>.
                We'll respond on or before <strong>{new Date(done.sla_deadline).toLocaleDateString("en-GB")}</strong>.
                You'll receive a verification email shortly.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl border-slate-200 shadow-xl shadow-slate-900/[0.04]">
            <CardContent className="p-8">
              <form className="space-y-5" onSubmit={submit}>
                <div className="space-y-1.5">
                  <Label>Request type</Label>
                  <select
                    value={form.type}
                    onChange={(e) => onChange("type", e.target.value)}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid="dsar-type"
                  >
                    {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Full name *</Label>
                    <Input required value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} data-testid="dsar-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email *</Label>
                    <Input required type="email" value={form.email} onChange={(e) => onChange("email", e.target.value)} data-testid="dsar-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Phone (optional — helps us match records)</Label>
                  <Input type="tel" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} data-testid="dsar-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label>Statement (optional)</Label>
                  <Textarea
                    rows={4}
                    value={form.statement}
                    onChange={(e) => onChange("statement", e.target.value)}
                    placeholder="Tell us what you need — e.g. specific date range, particular form/lander, or a concern that prompted this request."
                    data-testid="dsar-statement"
                  />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="dsar-submit">
                  {busy ? <Loader2 className="size-4 animate-spin" /> : "Submit request"}
                </Button>
                <p className="text-[11.5px] text-slate-500 leading-relaxed text-center">
                  By submitting, you confirm the details above relate to you. We may ask for additional ID
                  to verify identity per ICO guidance.
                </p>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
